<template>
  <!-- ITEM LINK
      (ln 9 a 18): Atributos e classes são aplicadas de acordo com as propriedades escolhidas para o Item
      (ln 19) Caso a prop hover seja atribuída ao item, o atributo data-toggle="selection" será aplicado.
              Não funciona apenas para destacar o item com hover, mas para que o elemento seja acionado com clique
              obs: O exemplo de item com checkbox nos wbc não está funcionando corretamente. Comparar com exemplo no DS - 'Itens de seleção'
     -->
  <a
    v-if="href"
    v-tooltip="{ text: tooltipText, place: tooltipPlace, type: tooltipType, timer: tooltipTimer }"
    v-bind="$attrs"
    :href="href"
    :title="title"
    class="br-item"
    :class="{
      active: active,
      disabled: disabled,
      selected: selected,
    }"
    :data-toggle="hover ? 'selection' : null"
  >
    <!--
        Atribui a classe 'content' a todo o conteúdo inserido no item link
        Não faz parte do item tipo link, de acordo com exemplos no DS
    -->
    <div class="content"><slot /></div>
  </a>

  <!-- ITEM !== LINK
    obs: Existem links também do tipo botão, de acordo com exemplo no DS
    (ln 41 a 44): Atributos e classes são aplicadas de acordo com as propriedades escolhidas para o Item
    (ln 39) A classe 'align-items-center' não faz parte do componente item no DS
    (ln 45 e 46) Recebe a densidade do pai (List ou qq outro) e aplica as classes 'py-3' para medium e 'py-4' para small
    (ln 48 a 51) Recebe 'data-toggle' (boolean) do List e aplica atributos ao Item (data-toggle e role)
    (ln 52) No clique do item, a classe open é aplicada ou retirada, bem como o ícone seta para cima e para baixo (ln 59 a 64)
  -->
  <div
    v-else
    v-tooltip="{ text: tooltipText, place: tooltipPlace, type: tooltipType, timer: tooltipTimer }"
    v-bind="$attrs"
    class="br-item align-items-center"
    :class="{
      active: active,
      disabled: disabled,
      selected: selected,
      open: isOpen,
      'py-3': parentDensity === 'medium',
      'py-4': parentDensity === 'small',
    }"
    :role="dataToggleParentBrList ? 'listitem' : null"
  >
    <!-- (ln 55 a 74) Ajuste do conteúdo do Item de acordo com a prop 'data-toggle' do List
         (ln 60) Ajuste do título do conteúdo para que seja possível inserir outros elementos diferentes da prop 'title'
                 no componente cookiebar. Sem o ajuste a inclusão de outros elementos no slot default resultaria em conteúdo e não header/título do item
    -->
    <div v-if="dataToggleParentBrList" class="content">
      <div class="flex-fill">
        {{ title }}
        <slot name="content-header" />
      </div>
      <br-button
        circle
        type="tertiary"
        density="small"
        :icon="isOpen ? 'chevron-up' : 'chevron-down'"
        class="ml-1"
        :data-toggle="dataToggleParentBrList ? 'collapse' : hover ? 'selection' : null"
        @click="dataToggleParentBrList ? toggleOpen() : void 0"
      ></br-button>
    </div>
    <span v-if="!dataToggleParentBrList">
      <slot />
    </span>
  </div>
  <span v-show="dataToggleParentBrList && isOpen">
    <slot />
  </span>
</template>

<script>
import TooltipMixin from "../../mixins/TooltipMixin"
import BrButton from "../Button/Button.ce.vue"
import IconBase from "../IconBase/IconBase.ce.vue"

const Item = {
  name: "br-item",
  components: {
    BrButton,
    IconBase,
  },
  mixins: [TooltipMixin],
  inject: {
    parentList: {
      default: null,
    },
    parentDensity: {
      default: "small",
    },
  },
  provide() {
    return {
      parentList: this,
      parentDensity: this.parentDensity,
    }
  },
  props: {
    /**
     * **[OPCIONAL]** Coloca o item como ênfase, destacando-o dos demais.
     * */
    active: {
      type: Boolean,
      default: false,
    },
    /**
     * **[OPCIONAL]** Efeito Hover, destaca o item ao passar o mouse por cima do mesmo.
     */
    hover: {
      type: Boolean,
      default: false,
    },
    /**
     * **[OPCIONAL]** Desabilita o item, mudando o cursor do mouse para símbolo de desabilitado e oculta o item para clique.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * **[OPCIONAL]** Destaca o item como selecionado. Possui um watcher que dispara um evento quando o seu valor é modificado.
     */
    selected: {
      type: Boolean,
      default: false,
    },
    /**
     * **[OPCIONAL]** Indica se o item está aberto/fechado quando o br-list pai é data-toggle. Possui um watcher que dispara um evento quando o seu valor é modificado.
     * */
    open: {
      type: Boolean,
      default: false,
    },
    /**
     * **[OPCIONAL]** Título visível para o item quando o br-list pai é data-toggle.
     * */
    title: {
      type: String,
      default: null,
    },
    /**
     * **[OPCIONAL]** URL ou PATH pelo qual o usuario sera encaminhado ao clicar no item. Adicionar um link torna o item do tipo link.
     * */
    href: {
      type: String,
      default: null,
    },
  },
  emits: [
    /**
     * **[OPCIONAL]** Evento disparado quando a prop *selected* é modificada.
     *
     * Referência sobre o consumo de eventos:
     * [Eventos Emitidos pelos Componentes](/?path=/story/eventos-emitidos-pelos-componentes--page)
     */
    "toggle-selected",
    /**
     * **[OPCIONAL]** Evento disparado quando a prop *open* é modificada.
     *
     * Referência sobre o consumo de eventos:
     * [Eventos Emitidos pelos Componentes](/?path=/story/eventos-emitidos-pelos-componentes--page)
     */
    "toggle-open",
  ],
  data() {
    return {
      isOpen: this.open,
    }
  },
  computed: {
    dataToggleParentBrList: function () {
      return this.parentList && this.parentList.isDataToggle()
    },

    dataUniqueParentBrList: function () {
      return this.parentList && this.parentList.isDataToggle() && this.parentList.isDataUnique()
    },
  },
  watch: {
    /**

     * Dispara o evento toggle-selected quando a prop selected é modificada.
     */
    selected: function (newValue) {
      this.$emit("toggle-selected", newValue)
    },
    /**
     * Dispara o evento toggle-open quando a prop open é modificada.
     */
    open: function (newValue) {
      this.$emit("toggle-open", newValue)
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.dataToggleParentBrList) {
        this.parentList.addItem(this)
        this.isOpen = this.open
      } else {
        this.isOpen = true
      }
    })
  },
  methods: {
    toggleOpen() {
      if (!this.isOpen && this.dataUniqueParentBrList) {
        this.parentList.closeAllItems()
      }
      this.isOpen = !this.isOpen
    },
  },
}

export default Item
</script>

<style lang="scss">
@import "~@govbr-ds/core/src/partial/scss/_base";
@import "~@govbr-ds/core/src/partial/scss/_utilities";
@import "~@govbr-ds/core/src/components/item/_item";
@import "~@govbr-ds/core/src/components/item/_mixins";
@import "~@govbr-ds/core/src/components/tag/_tag";
@import "~@govbr-ds/core/src/components/button/_button";
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.11.2/css/all.min.css");

// Corrige a quebra de alinhamento no item quando o mesmo faz parte de uma lista horizontal, devido ao shadow-root.
:host {
  @include item-align-horizontal-list;
}
</style>
